/**
 * @kgc/packages - PackageAdmin read-DTO (Zod seam-kontraktus)
 *
 * READ-seam-freeze: a `package/02-admin` FE panel ehhez a Zod-kontraktushoz
 * pin-eli a proto-parity spec-et + MSW-mockot. A séma a VALÓS
 * `GET /api/v1/packages/:id` válasz-shape-t tükrözi (PackageWithItems),
 * a `PackageItemCategory` (FREE/RENTABLE/CONSIGNMENT) ADR-118 Am.2 D-1 szerint.
 *
 * STORY-1205 / ADR-084 / ADR-118 Am.2
 */

import { z } from 'zod';

// ============================================
// PACKAGE ITEM READ SCHEMA
// ============================================

export const PackageAdminItemSchema = z.object({
  id: z.string().uuid(),
  packageId: z.string().uuid(),
  itemType: z.enum(['PRODUCT', 'RENTAL_EQUIPMENT', 'ACCESSORY', 'SERVICE_ITEM']),
  settlement: z.enum(['INCLUDED', 'RENTAL', 'CONSIGNMENT']),
  sortOrder: z.number().int().min(0),
  productId: z.string().uuid().nullable(),
  rentalEquipmentId: z.string().uuid().nullable(),
  quantity: z.number().int().min(1),
  isOptional: z.boolean(),
  isSubstitutable: z.boolean(),
  isReturnable: z.boolean(),
  unitPrice: z.number().nullable(),
  notes: z.string().nullable(),
  // ADR-118 Am.2 D-1 / STORY-1205 — csomag-tétel besorolás és accordion mezők
  category: z.enum(['FREE', 'RENTABLE', 'CONSIGNMENT']),
  defaultQuantity: z.number().int().min(1),
  isQuantityEditable: z.boolean(),
  visibleOnWebshop: z.boolean(),
});

export type PackageAdminItem = z.infer<typeof PackageAdminItemSchema>;

// ============================================
// LINKED PRODUCT REF SCHEMA
// ============================================

const LinkedProductRefSchema = z.object({
  productId: z.string().uuid(),
  sortOrder: z.number().int(),
  product: z.object({
    id: z.string().uuid(),
    sku: z.string(),
    name: z.string(),
  }),
});

// ============================================
// PACKAGE READ SCHEMA
// ============================================

export const PackageAdminSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  packageCode: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  type: z.enum(['RENTAL', 'SALES', 'SERVICE', 'MIXED', 'PROMOTIONAL', 'FRANCHISE']),
  scope: z.enum(['CENTRAL', 'LOCAL']),
  pricingModel: z.enum(['FIXED', 'DISCOUNTED', 'TIERED']),
  fixedPrice: z.number().nullable(),
  discountPercent: z.number().nullable(),
  dailyRate: z.number().nullable(),
  weeklyRate: z.number().nullable(),
  monthlyRate: z.number().nullable(),
  depositAmount: z.number().nullable(),
  validFrom: z.coerce.date().nullable(),
  validTo: z.coerce.date().nullable(),
  isActive: z.boolean(),
  linkedEquipmentCategories: z.array(z.string()),
  linkingMode: z.enum(['CATEGORY', 'EQUIPMENT']),
  imageUrl: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  createdBy: z.string().uuid(),
  updatedBy: z.string().uuid(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
  items: z.array(PackageAdminItemSchema),
  linkedProducts: z.array(LinkedProductRefSchema),
});

export type PackageAdmin = z.infer<typeof PackageAdminSchema>;

// ============================================
// API RESPONSE WRAPPERS
// ============================================

/** GET /api/v1/packages/:id valasz-wrapper */
export const PackageAdminResponseSchema = z.object({
  data: PackageAdminSchema,
});

export type PackageAdminResponse = z.infer<typeof PackageAdminResponseSchema>;

/** GET /api/v1/packages lista-valasz wrapper */
export const PackageAdminListResponseSchema = z.object({
  data: z.array(PackageAdminSchema),
  meta: z.object({
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    limit: z.number().int().min(1),
    hasMore: z.boolean(),
  }),
});

export type PackageAdminListResponse = z.infer<typeof PackageAdminListResponseSchema>;
